import React, { FC, useEffect } from 'react';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useParams } from 'react-router-dom';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { useAppAuthenticationIsAuthenticated } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication';
import { useLocalStorage } from 'react-use';
import { useSetSelectedCustomerId as useKeycloakSetSelectedCustomerId } from '../../packages/iam/src/context/hooks';
import { ParametersProvider } from '../../packages/transaction/src/contexts/Parameters/ParametersProvider';
import { ErrorPage } from '../../packages/content-composer/src/pages';
import { NotificationsProvider } from '../../packages/authorizations/src';
import {
  useCustomerList,
  useSelectedCustomerId,
  useSetSelectedCustomerId,
  useUserInfo,
  useUserInfoLoadingState,
  useUserInfoState,
} from '../../packages/userProfile/src/contexts/UserInfo/hooks';
import { App } from './App';
import { chatbot } from './chatbot';

const ProvidersApp: FC<GenericObject> = ({ brandConfig }) => {
  const { customerId: customerIdParam } = useParams();
  const setSelectedCustomerId = useSetSelectedCustomerId();
  const setKeycloakSelectedCustomerId = useKeycloakSetSelectedCustomerId();
  const userInfo = useUserInfo();
  const customerList = useCustomerList();
  const selectedCustomerId = useSelectedCustomerId();
  const isLoadingEntitlementsData = useUserInfoLoadingState('isLoadingEntitlementsData');
  const { entitlements } = useUserInfoState();
  const authenticated = useAppAuthenticationIsAuthenticated();
  const isValidCustomerId =
    !!customerIdParam && !!customerList?.find(({ customerId }) => customerId === customerIdParam);
  const [defaultCustomerList, setDefaultCustomerList] = useLocalStorage<GenericObject | null>('customer-list', null);

  useEffect(() => {
    if (isValidCustomerId && !!userInfo) {
      if (customerIdParam !== selectedCustomerId) {
        setSelectedCustomerId(customerIdParam as string);
        setKeycloakSelectedCustomerId(customerIdParam as string);
        setDefaultCustomerList(prev => ({ ...prev, [userInfo?.omniUserId]: customerIdParam as string }));
      }
    }
  }, [
    customerIdParam,
    isValidCustomerId,
    setSelectedCustomerId,
    setDefaultCustomerList,
    selectedCustomerId,
    userInfo,
    setKeycloakSelectedCustomerId,
  ]);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === 'uat' && userInfo) {
      console.log('ENVIRONMENT', process.env.REACT_APP_ENVIRONMENT); // TODO: remove log
      chatbot(userInfo);
    }
  }, [userInfo]);

  if (!authenticated || (selectedCustomerId && isEmpty(entitlements) && !isLoadingEntitlementsData)) {
    return <ErrorPage pageConfig={brandConfig.errorPage} code='401' />;
  }

  return isValidCustomerId && selectedCustomerId && entitlements.length > 0 ? (
    <ParametersProvider>
      <NotificationsProvider>
        <App brandConfig={brandConfig} />
      </NotificationsProvider>
    </ParametersProvider>
  ) : null;
};

export { ProvidersApp };
